/**
 * SB2-W-16 — TanStack Query hooks for the projects feature.
 *
 * Hierarchical keys + per-query `staleTime`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { projectsFetch } from "./projectsFetch.js";
import { projectsKeys } from "./keys.js";
import type {
  CreateProjectRequest,
  ListProjectsParams,
  ProjectDetail,
  ProjectListItem,
  ProjectListResponse,
  ProjectsFacetResponse,
  ProjectTurbineLocations,
  UpdateProjectRequest,
  UpdateProjectWorkflowRequest,
  UpdateProjectWorkflowResponse,
} from "./types.js";
import type { ProjectsComponents } from "@aeriosiq/types";

function listProjectsUrl(params: ListProjectsParams): string {
  const sp = new URLSearchParams();
  sp.set("page", String(params.page ?? 1));
  sp.set("pageSize", String(params.pageSize ?? 50));
  if (params.q && params.q.length > 0) sp.set("q", params.q);
  if (params.customerIds && params.customerIds.length > 0) {
    sp.set("customerIds", params.customerIds.join(","));
  }
  if (params.contractorIds && params.contractorIds.length > 0) {
    sp.set("contractorIds", params.contractorIds.join(","));
  }
  // SB4-05 / F-05 — explicit state multi-select. CSV-encoded on the
  // wire (matches the customerIds/contractorIds convention). When set,
  // the BE takes it as precedence over `includeCompleted`.
  if (params.states && params.states.length > 0) {
    sp.set("state", params.states.join(","));
  }
  // SB2-W-16 AC2 — explicit false; the BE defaults to false too, but
  // we set it on the wire so the URL is unambiguous in logs.
  sp.set("includeCompleted", params.includeCompleted ? "true" : "false");
  if (params.sortBy) sp.set("sortBy", params.sortBy);
  if (params.sortDir) sp.set("sortDir", params.sortDir);
  // TNCY-4 (2.4.0): scope=member fans the list out across granted
  // cross-tenant projects. Only sent when explicitly `member` — omitting
  // it leaves the BE on its `own` default (no wire churn for own-only
  // callers).
  if (params.scope === "member") sp.set("scope", "member");
  // PM-WF-01 (2.25.0): the "Workflow not set" filter (own scope only).
  if (params.workflow && params.scope !== "member") {
    sp.set("workflow", params.workflow);
  }
  return `/projects?${sp.toString()}`;
}

export function useProjectsListQuery(params: ListProjectsParams) {
  return useQuery<ProjectListResponse>({
    queryKey: projectsKeys.list(params),
    queryFn: () => projectsFetch<ProjectListResponse>(listProjectsUrl(params)),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * PM-4 — `POST /projects` (`createProject`, projects/v1@2.7.0). Creates
 * the project base; party `*_tenant_id` fields auto-grant on the BE
 * (XT-D23, PM-D23). Invalidates the list + customer/contractor facets so
 * the new project (and any new party tenants) appear after navigation.
 */
export function useCreateProjectMutation() {
  const qc = useQueryClient();
  return useMutation<ProjectDetail, Error, CreateProjectRequest>({
    mutationFn: (body) =>
      projectsFetch<ProjectDetail>("/projects", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKeys.lists() });
      void qc.invalidateQueries({ queryKey: projectsKeys.facets() });
    },
  });
}

/**
 * PMGT (projects/v1@2.12.0) — `PATCH /projects/{projectId}` base-field update
 * (`updateProjectParties`, base-field group). Writes whichever of
 * projectName / startDate / endDate / offerReference / reportLanguage /
 * description are present; the 200 returns a `ProjectListItem` (back-compat).
 * Since the list row isn't the full base, we re-fetch the detail by
 * invalidating its cache key so the project-edit Basics step re-reads the
 * canonical persisted values. Also invalidates the list (the row changed).
 */
export function useUpdateProjectMutation(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectListItem, Error, UpdateProjectRequest>({
    mutationFn: (body) =>
      projectsFetch<ProjectListItem>(
        `/projects/${encodeURIComponent(projectId)}`,
        { method: "PATCH", body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      void qc.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * DL-01 decision 2 (projects/v1 2.27.0, `updateProjectCustomerImageDownload`)
 * — `PATCH /projects/{projectId}/customer-image-download` with
 * `UpdateCustomerImageDownloadRequest`; the 200 is the `ProjectDetail`,
 * seeded into the detail cache. Owner tenant_admin / project_manager
 * (platform_admin) only — a customer gets a 404.
 */
export function useSetCustomerImageDownloadMutation(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectDetail, Error, boolean>({
    mutationFn: (customerImageDownload) => {
      const body: ProjectsComponents["schemas"]["UpdateCustomerImageDownloadRequest"] =
        { customerImageDownload };
      return projectsFetch<ProjectDetail>(
        `/projects/${encodeURIComponent(projectId)}/customer-image-download`,
        { method: "PATCH", body },
      );
    },
    onSuccess: (detail) => {
      qc.setQueryData(projectsKeys.detail(projectId), detail);
      void qc.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * PM-D38 — single-project detail read (`GET /projects/{projectId}`,
 * projects/v1@2.10.0, `getProject`). The source for the AI-models edit
 * panel's prefill: the `ProjectDetail` carries the per-project `aiModelIds`
 * selection. `enabled`-gated so the panel only fetches when it has a real
 * projectId. The PUT mutation below seeds this same cache key with its 200
 * `ProjectDetail` response, so a save immediately re-prefills without a
 * refetch.
 */
export function useProjectDetailQuery(projectId: string | null | undefined) {
  return useQuery<ProjectDetail>({
    queryKey: projectsKeys.detail(projectId ?? ""),
    enabled: projectId != null && projectId.length > 0,
    queryFn: () =>
      projectsFetch<ProjectDetail>(
        `/projects/${encodeURIComponent(projectId ?? "")}`,
      ),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * FB-14 (projects/v1@2.11.0) — `GET /projects/{projectId}/turbines`
 * (`getProjectTurbines`) → `ProjectTurbineLocations`. The project's distinct
 * contract-position turbines with asset→site coordinate fallback, for the
 * project-detail map. Unpaginated (hard cap 2000 on the BE).
 *
 * Tenant-readiness `enabled`-gate: the request carries `X-Tenant-ID`, so it
 * must not fire until the resolved tenant matches the session's tenant — a
 * mid-switch race would 400 on a stale header (`reference_tenant_scoped_query_
 * enabled_gate`).
 */
export function useProjectTurbinesQuery(projectId: string) {
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantReady =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  return useQuery<ProjectTurbineLocations>({
    queryKey: projectsKeys.turbines(projectId),
    enabled: tenantReady && projectId.length > 0,
    queryFn: () =>
      projectsFetch<ProjectTurbineLocations>(
        `/projects/${encodeURIComponent(projectId)}/turbines`,
      ),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * PM-D38 — `PUT /projects/{projectId}/ai-models` (`replaceProjectAiModels`,
 * projects/v1@2.10.0). FULL-REPLACE the project's per-project AI-model
 * selection with a flat `aiModelIds` list (no role split). Returns the updated
 * `ProjectDetail` (with the new `aiModelIds` + re-derived deprecated booleans),
 * which we write into the detail cache so the panel re-prefills without a
 * refetch. Also invalidates the detail + the list (the list row's derived
 * booleans may change).
 */
export function useReplaceProjectAiModelsMutation(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectDetail, Error, string[]>({
    mutationFn: (aiModelIds) =>
      projectsFetch<ProjectDetail>(
        `/projects/${encodeURIComponent(projectId)}/ai-models`,
        { method: "PUT", body: { aiModelIds } },
      ),
    onSuccess: (detail) => {
      qc.setQueryData(projectsKeys.detail(projectId), detail);
      void qc.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      void qc.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

/**
 * PM-WF-01 — `PATCH /projects/{projectId}/workflow` (`updateProjectWorkflow`,
 * projects/v1@2.26.0). A plain function (not a hook) so the bulk "Set
 * workflow…" dialog can fan out ONE request per project (the API has no bulk
 * endpoint, by decision). M3C-04 — sends the picked VERSION
 * (`workflowVersionId`, M3C-03); the server writes the version and its preset
 * key together. Errors carry the BE envelope (`status`, `body.reason` =
 * `workflow_not_available`).
 */
export function patchProjectWorkflow(
  projectId: string,
  workflowVersionId: string,
): Promise<UpdateProjectWorkflowResponse> {
  const body: UpdateProjectWorkflowRequest = { workflowVersionId };
  return projectsFetch<UpdateProjectWorkflowResponse>(
    `/projects/${encodeURIComponent(projectId)}/workflow`,
    { method: "PATCH", body },
  );
}

/** PM-WF-01 — the single-project PATCH for the detail page's `WorkflowPanel`.
 *  Writes the returned `ProjectDetail` into the detail cache and invalidates
 *  the lists (the "not set" badge / filter count change). */
export function useUpdateProjectWorkflowMutation(projectId: string) {
  const qc = useQueryClient();
  return useMutation<UpdateProjectWorkflowResponse, Error, string>({
    mutationFn: (versionId) => patchProjectWorkflow(projectId, versionId),
    onSuccess: (res) => {
      qc.setQueryData(projectsKeys.detail(projectId), res.project);
      void qc.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
}

export function useProjectsCustomerFacetQuery() {
  return useQuery<ProjectsFacetResponse>({
    queryKey: projectsKeys.customerFacet(),
    queryFn: () =>
      projectsFetch<ProjectsFacetResponse>("/projects/facets/customers"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useProjectsContractorFacetQuery() {
  return useQuery<ProjectsFacetResponse>({
    queryKey: projectsKeys.contractorFacet(),
    queryFn: () =>
      projectsFetch<ProjectsFacetResponse>("/projects/facets/contractors"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
